import React, { FC, useEffect, useMemo, useState } from "react";
import { RouteComponentProps, useNavigate, useParams } from "@reach/router";
import { DateTime } from "luxon";
import {
  HeadingLarge,
  HeadingSmall,
  LabelMedium,
  ParagraphMedium,
} from "kanopi-components/typography";
import { Block } from "kanopi-components/Block";
import { PortalRow, RowGap } from "kanopi-components/PortalRow";
import { PolicySummaryCard } from "../components/PolicySummaryCard";
import { TTableRow, TwoColumnTable } from "kw/components/TabularDisplay/TwoColumnTable";
import { valueTypes } from "kw/util/useFormatter";
import { PolicyFixedFooter } from "../components/PolicyFixedFooter";
import { NavButton } from "kw/components/NavButton/NavButton";
import {
  useConfirmPolicyCancellation,
  usePolicyCancellationQuote,
} from "../hooks/useCancelPolicy";
import { Placeholder } from "kanopi-components/Placeholder";
import { ConfirmationCircle } from "kw/util/ConfirmationCircle";
import { GraphQLTypes } from "kw/api/zeus";
import { Currency } from "kanopi-components/Currency";
import { FeeSummary } from "../components/FeeSummary";

type TConfirmPolicyCancelPage = {
  policyId: string;
};
export const PolicyCancellationSummaryPage: FC<RouteComponentProps> = () => {
  const params = useParams<TConfirmPolicyCancelPage>();
  const {
    fetchPolicyCancellationQuote,
    data: cancellationQuote,
    loading: cancellationQuoteLoading,
    isError,
  } = usePolicyCancellationQuote(params?.policyId);
  const {
    confirmPolicyCancellation,
    loading: confirmCancellationInProgress,
    isCancellationSuccess: confirmCancellationSubmitSuccess,
  } = useConfirmPolicyCancellation();
  const navigate = useNavigate();
  const [fees, setFees] = useState<{
    mappedRows: TTableRow[];
    summary: TTableRow;
  }>();
  const [policyDetails, setPolicyDetails] = useState<TPolicy>();
  const handleConfirmCancellation = () => {
    confirmPolicyCancellation(params?.policyId, cancellationQuote?.id);
  };
  useEffect(() => {
    if (params?.policyId) {
      fetchPolicyCancellationQuote();
    }
  }, [params?.policyId]);
  // useEffect(() => {
  //   setPolicyDetails(cancellationQuote?.policy as TPolicy);
  //   setFees(twoColumnCancellationMapper(cancellationQuote));
  // }, [cancellationQuote]);
  useEffect(() => {
    if (confirmCancellationSubmitSuccess) {
      navigate(`/policies/${params?.policyId}/cancel/success`);
    }
  }, [confirmCancellationSubmitSuccess]);
  const policyCancellationDate = useMemo(() => {
    if (cancellationQuote) {
      return DateTime.fromFormat(
        cancellationQuote.effectiveDate,
        "yyyy-MM-dd"
      ).toLocaleString({ month: "long", day: "numeric", year: "numeric" });
    }
  }, [cancellationQuote]);

  return (
    <Block
      display="flex"
      justifyItems="center"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Placeholder loading={cancellationQuoteLoading}>
        <PortalRow fullWidth={true} maxWidth="23rem">
          {policyDetails && (
            <PolicySummaryCard policy={policyDetails} splitCard={true} ctaTitle="" />
          )}
        </PortalRow>
        <PortalRow>
          <HeadingLarge margin={0} center>
            Your policy will be cancelled on {policyCancellationDate}
          </HeadingLarge>
          <ParagraphMedium center>
            We will {cancellationQuote?.amountDue <= 0 ? "refund" : "charge"}{" "}
            <Currency value={Math.abs(cancellationQuote?.amountDue)} /> to your saved
            payment card on {policyCancellationDate}.
          </ParagraphMedium>
          <ParagraphMedium center>
            This includes any pro rata refund of premiums you have already paid for the
            period after your cancellation effective date, minus any cancellation fees.
          </ParagraphMedium>

          <ParagraphMedium center>
            You cannot undo a policy cancellation, so please confirm you want to cancel
            before proceeding.
          </ParagraphMedium>
        </PortalRow>
        <PolicyFixedFooter>
          <NavButton
            disabled={isError}
            onClick={handleConfirmCancellation}
            isLoading={confirmCancellationInProgress}
          >
            Confirm cancellation
          </NavButton>
        </PolicyFixedFooter>
      </Placeholder>
    </Block>
  );
};

export const PolicyCancellationSuccessPage: FC<RouteComponentProps> = () => {
  const params = useParams<TConfirmPolicyCancelPage>();
  const navigate = useNavigate();
  const gotoPolicyHome = () => {
    navigate("/policies");
  };
  const {
    fetchPolicyCancellationQuote,
    data: cancellationQuote,
    loading: cancellationQuoteLoading,
    isError,
  } = usePolicyCancellationQuote(params?.policyId);

  useEffect(() => {
    if (params?.policyId) {
      fetchPolicyCancellationQuote();
    }
  }, [params?.policyId]);

  const policyCancellationDate = useMemo(() => {
    if (cancellationQuote) {
      return DateTime.fromFormat(
        cancellationQuote.effectiveDate,
        "yyyy-MM-dd"
      ).toLocaleString({ month: "long", day: "numeric", year: "numeric" });
    }
  }, [cancellationQuote]);

  return (
    <Block
      display="flex"
      justifyItems="center"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Placeholder loading={cancellationQuoteLoading}>
        <PortalRow>
          <ConfirmationCircle />
        </PortalRow>
        <PortalRow bottomSpace={RowGap.Standard}>
          <Block display="flex" flexDirection="column">
            <LabelMedium center>Cancellation Successful</LabelMedium>
            <HeadingLarge center margin={0}>
              Your policy will be cancelled on {policyCancellationDate}
            </HeadingLarge>
          </Block>
        </PortalRow>
        <PortalRow>
          <ParagraphMedium center>
            We’ve sent you an email with your cancellation confirmation.
          </ParagraphMedium>
          <ParagraphMedium center>
            Please don’t hesitate to contact us if you have any questions.
          </ParagraphMedium>
        </PortalRow>
        <PolicyFixedFooter>
          <NavButton onClick={gotoPolicyHome}>Done</NavButton>
        </PolicyFixedFooter>
      </Placeholder>
    </Block>
  );
};
